(() => {
  if (customElements.get('pellegrini-cart')) return;

  class PellegriniCart extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.work = Promise.resolve();
      this.busy = false;
      this.pending = 0;
      this.noteDraft = this.querySelector('[data-note]')?.value;
      this.savedNote = this.noteDraft;
      const options = { signal: this.events.signal };
      this.addEventListener('click', event => this.onClick(event), options);
      this.addEventListener('input', event => this.onInput(event), options);
      this.addEventListener('focusout', event => this.onFocusOut(event), options);
      this.addEventListener('submit', event => this.onSubmit(event), options);
      const onExternalChange = event => {
        if (event.detail?.baseEvent === 'pellegrini:cart') return;
        if (this.busy) this.refreshNeeded = true;
        else this.enqueue(() => this.refresh());
      };
      document.addEventListener('cart:change', onExternalChange, options);
      document.addEventListener('cart:refresh', onExternalChange, options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.enqueue(() => this.refresh());
      }, options);
      this.dataset.ready = '';
      this.updateControls();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
    }

    rows() {
      return Array.from(this.querySelectorAll('[data-cart-line]'));
    }

    rowFor(key) {
      return this.rows().find(row => row.dataset.key === key);
    }

    rules(row) {
      const maximum = row.dataset.max;
      return {
        step: Math.max(1, Number(row.dataset.step) || 1),
        min: Math.max(1, Number(row.dataset.min) || 1),
        max: maximum === undefined || maximum === '' ? Infinity : Math.max(0, Number(maximum) || 0),
        eligible: row.dataset.eligible === 'true'
      };
    }

    validateRow(row) {
      const input = row.querySelector('[data-quantity]');
      const rules = this.rules(row);
      const quantity = input.valueAsNumber;
      let error = '';
      if (!rules.eligible) error = 'Bitte wählen Sie diesen Artikel mit dem aktuell verfügbaren Jahrgang erneut aus.';
      else if (rules.max < rules.min) error = 'Die hinterlegte Mengenregel lässt für diese Position keine weitere Verpackungseinheit zu.';
      else if (!Number.isSafeInteger(quantity) || quantity < rules.min || quantity % rules.step !== 0) {
        error = `Bitte wählen Sie mindestens ${rules.min} Stück in Schritten von ${rules.step}.`;
      } else if (quantity > rules.max) error = `Für diese Position gilt eine Höchstmenge von ${rules.max} Stück.`;
      input.setCustomValidity(error);
      input.setAttribute('aria-invalid', error ? 'true' : 'false');
      return !error;
    }

    updateControls() {
      let valid = true;
      for (const row of this.rows()) {
        const rules = this.rules(row);
        const input = row.querySelector('[data-quantity]');
        const quantity = input.valueAsNumber;
        const rowValid = this.validateRow(row);
        valid = rowValid && valid;
        input.disabled = this.busy;
        const down = row.querySelector('[data-step-change="-1"]');
        const up = row.querySelector('[data-step-change="1"]');
        if (down) down.disabled = this.busy || !rules.eligible || quantity <= rules.min || rules.max < rules.min;
        if (up) up.disabled = this.busy || !rules.eligible || quantity >= rules.max || rules.max < rules.min;
        row.querySelectorAll('[data-add-quantity]').forEach(button => {
          const increment = Number(button.dataset.addQuantity);
          button.disabled = this.busy || !rowValid || !Number.isSafeInteger(increment) || increment < 1 || quantity + increment > rules.max;
        });
        row.querySelector('[data-remove]')?.setAttribute('aria-disabled', this.busy ? 'true' : 'false');
      }
      const checkout = this.querySelector('[data-checkout]');
      if (checkout) checkout.disabled = this.busy || !valid || this.rows().length === 0;
      const note = this.querySelector('[data-note]');
      if (note) note.disabled = this.busy;
      this.setAttribute('aria-busy', this.busy ? 'true' : 'false');
      return valid;
    }

    status(message, isError = false) {
      const status = this.querySelector('[data-cart-status]');
      if (!status) return;
      status.textContent = message;
      status.toggleAttribute('data-error', isError);
    }

    onInput(event) {
      if (event.target.matches('[data-note]')) this.noteDraft = event.target.value;
      if (event.target.matches('[data-quantity]')) this.updateControls();
    }

    onFocusOut(event) {
      const next = event.relatedTarget;
      // Let the intended button handle the draft first; saving on blur must not swallow its click.
      if (next && this.contains(next) && next.closest('button,[data-remove]')) return;
      if (event.target.matches('[data-quantity]')) {
        const row = event.target.closest('[data-cart-line]');
        if (!this.validateRow(row)) {
          this.status(event.target.validationMessage, true);
          return;
        }
        const quantity = event.target.valueAsNumber;
        if (quantity !== Number(row.dataset.savedQuantity)) this.enqueue(() => this.change(row.dataset.key, quantity, next?.id));
      } else if (event.target.matches('[data-note]')) {
        if (next && this.contains(next)) return;
        this.enqueue(() => this.saveNote());
      }
    }

    onClick(event) {
      const button = event.target.closest('[data-step-change],[data-add-quantity],[data-remove]');
      if (!button || !this.contains(button)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      if (this.busy || button.disabled) return;
      const row = button.closest('[data-cart-line]');
      const key = row.dataset.key;
      if (button.hasAttribute('data-remove')) {
        this.enqueue(() => this.change(key, 0));
        return;
      }
      const input = row.querySelector('[data-quantity]');
      const rules = this.rules(row);
      let quantity = Number.isFinite(input.valueAsNumber) ? input.valueAsNumber : rules.min;
      if (button.hasAttribute('data-add-quantity')) quantity += Number(button.dataset.addQuantity);
      else if (Number(button.dataset.stepChange) > 0) quantity = (Math.floor(quantity / rules.step) + 1) * rules.step;
      else quantity = (Math.ceil(quantity / rules.step) - 1) * rules.step;
      quantity = Math.max(rules.min, Math.min(rules.max, quantity));
      input.value = String(quantity);
      if (!this.validateRow(row)) {
        this.status(input.validationMessage, true);
        this.updateControls();
        return;
      }
      this.enqueue(() => this.change(key, quantity));
    }

    async onSubmit(event) {
      if (!event.target.matches('[data-cart-form]')) return;
      if (this.nativeSubmit) {
        this.nativeSubmit = false;
        return;
      }
      event.preventDefault();
      if (this.busy) return;
      if (!this.updateControls()) {
        const invalid = this.rows().find(row => !this.validateRow(row));
        const input = invalid?.querySelector('[data-quantity]');
        this.status(input?.validationMessage || 'Bitte prüfen Sie die markierten Artikel.', true);
        input?.focus();
        input?.reportValidity();
        return;
      }
      const checkoutRequested = event.submitter?.name === 'checkout';
      await this.enqueue(async () => {
        const dirty = this.rows().find(row => row.querySelector('[data-quantity]').valueAsNumber !== Number(row.dataset.savedQuantity));
        if (dirty) await this.change(dirty.dataset.key, dirty.querySelector('[data-quantity]').valueAsNumber);
        await this.saveNote();
        await this.refresh();
        this.busy = false;
        if (!this.updateControls()) {
          this.status('Die Verfügbarkeit hat sich geändert. Bitte prüfen Sie Ihre Auswahl.', true);
          return;
        }
        if (!checkoutRequested) {
          this.status('Ihr Warenkorb wurde aktualisiert.');
          return;
        }
        const form = this.querySelector('[data-cart-form]');
        const button = form?.querySelector('[data-checkout]');
        if (!button || button.disabled) {
          this.status('Bitte melden Sie sich an und prüfen Sie Ihren Warenkorb erneut.', true);
          return;
        }
        this.status('Ihre Auswahl ist geprüft. Der Checkout wird geöffnet.');
        this.nativeSubmit = true;
        form.requestSubmit(button);
      });
    }

    enqueue(task) {
      this.pending += 1;
      this.busy = true;
      this.updateControls();
      this.work = this.work.catch(() => {}).then(async () => {
        try {
          if (!this.isConnected) return;
          this.busy = true;
          this.updateControls();
          await task();
          if (this.refreshNeeded) {
            this.refreshNeeded = false;
            await this.refresh();
          }
        } catch (error) {
          try { await this.refresh(); } catch {}
          this.status(error.message || 'Die Änderung konnte nicht bestätigt werden. Bitte laden Sie den Warenkorb neu.', true);
        } finally {
          this.pending -= 1;
          this.busy = this.pending > 0;
          if (this.isConnected) {
            this.updateControls();
            if (!this.busy && this.focusAfterChange) {
              const focusTarget = document.getElementById(this.focusAfterChange);
              if (focusTarget && this.contains(focusTarget)) focusTarget.focus({ preventScroll: true });
              this.focusAfterChange = null;
            }
          }
        }
      });
      return this.work;
    }

    async json(url, data) {
      const options = {
        method: data ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: data ? { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } : {},
        signal: AbortSignal.timeout(15000)
      };
      if (data) {
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(data);
      }
      let response;
      try { response = await fetch(url, options); }
      catch { throw new Error('Die Verbindung wurde unterbrochen. Bitte prüfen Sie die angezeigten Mengen, bevor Sie fortfahren.'); }
      let result;
      try { result = await response.json(); }
      catch { throw new Error('Der Warenkorb konnte nicht gelesen werden. Bitte laden Sie die Seite neu.'); }
      if (!response.ok) throw new Error('Die Änderung konnte nicht gespeichert werden. Bitte prüfen Sie die Menge oder versuchen Sie es erneut.');
      return result;
    }

    render(html) {
      if (typeof html !== 'string' || !html.trim()) return false;
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const incoming = documentFragment.querySelector('pellegrini-cart [data-cart-content]');
      const current = this.querySelector('[data-cart-content]');
      if (!incoming || !current || !this.isConnected) return false;
      current.replaceWith(incoming);
      const note = this.querySelector('[data-note]');
      if (note && typeof this.noteDraft === 'string') note.value = this.noteDraft;
      this.updateControls();
      return true;
    }

    async refresh() {
      const url = new URL(window.location.href);
      url.searchParams.delete('section_id');
      url.searchParams.set('sections', this.dataset.sectionId);
      const sections = await this.json(url.toString());
      if (!this.render(sections[this.dataset.sectionId])) throw new Error('Die aktuelle Auswahl konnte nicht geladen werden. Bitte laden Sie den Warenkorb neu.');
    }

    async change(key, quantity, focusId) {
      const row = this.rowFor(key);
      if (!row) throw new Error('Ihr Warenkorb hat sich geändert. Bitte prüfen Sie die aktuelle Auswahl.');
      const index = this.rows().indexOf(row);
      const rules = this.rules(row);
      if (quantity !== 0 && (!rules.eligible || !Number.isSafeInteger(quantity) || quantity < rules.min || quantity > rules.max || quantity % rules.step !== 0)) {
        throw new Error('Bitte wählen Sie eine Menge in ganzen Verpackungseinheiten.');
      }
      await this.saveNote();
      this.status('Ihre Auswahl wird aktualisiert …');
      const cart = await this.json(this.dataset.changeUrl, {
        id: key,
        quantity,
        sections: [this.dataset.sectionId],
        sections_url: window.location.pathname + window.location.search
      });
      if (!this.render(cart.sections?.[this.dataset.sectionId])) await this.refresh();
      const nextRows = this.rows();
      const target = quantity === 0 ? nextRows[Math.min(index, nextRows.length - 1)] : this.rowFor(key) || nextRows[index];
      const input = target?.querySelector('[data-quantity]');
      if (input && target.dataset.eligible === 'true') {
        this.focusAfterChange = focusId || input.id;
      }
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { cart, baseEvent: 'pellegrini:cart' }
      }));
      const actual = target ? Number(target.dataset.savedQuantity) : 0;
      if (quantity !== 0 && actual !== quantity) this.status('Shopify hat die Menge geändert. Bitte prüfen Sie Ihre Auswahl.', true);
      else this.status(quantity === 0 ? 'Der Artikel wurde entfernt.' : 'Ihre Auswahl wurde aktualisiert.');
    }

    async saveNote() {
      const input = this.querySelector('[data-note]');
      if (!input) return;
      const note = input.value;
      this.noteDraft = note;
      if (note === this.savedNote) return;
      await this.json(this.dataset.updateUrl, { note });
      this.savedNote = note;
    }
  }

  customElements.define('pellegrini-cart', PellegriniCart);
})();
